/**
 * Playlists a user explicitly chose to import.
 *
 * After PR12, library metadata lives at
 * `users/{uid}/playlists/yt:{rawYouTubePlaylistId}`. Bare
 * `users/{uid}/playlists/{rawYouTubePlaylistId}` documents are migrated lazily
 * on first library load. Listing accepts bare-only, namespaced-only, and dual
 * documents and returns one logical playlist.
 *
 * These documents are private to their owner: rules scope them to the uid, so
 * one participant's playlists never become visible to the rest of the party.
 */
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { userPlaylistId } from '../catalog/ids';
import { migrateOnRead } from '../catalog/migrations';
import { db } from '../firebase';
import { describeError } from '../utils/errorLog';
import { releasePlaylistRemoval } from './playlistRemoval';
import {
  dedupeLibraryPlaylistRecords,
  isBareYouTubePlaylistDoc,
  isNativePlaylistId,
  isPhase4PlaylistDoc,
  migrateBareYoutubePlaylists,
  namespacedYouTubePlaylistId,
  toLibraryListPlaylist,
} from './playlistIdMigration';

function playlistsCollection(uid) {
  return collection(db, 'users', uid, 'playlists');
}

export function buildImportedPlaylist(playlist) {
  if (!playlist?.playlistId) return null;
  return {
    playlistId: playlist.playlistId,
    title: typeof playlist.title === 'string' ? playlist.title : '',
    thumbnail: typeof playlist.thumbnail === 'string' ? playlist.thumbnail : '',
    itemCount: Number(playlist.itemCount) || 0,
    source: playlist.source || 'youtube',
  };
}

function catalogIdForImport(playlistId) {
  const raw = typeof playlistId === 'string' ? playlistId.trim() : '';
  if (!raw || isNativePlaylistId(raw) || (raw.includes(':') && !raw.startsWith('yt:'))) {
    return raw;
  }
  return namespacedYouTubePlaylistId(raw);
}

function rawIdForImport(playlistId) {
  const raw = typeof playlistId === 'string' ? playlistId.trim() : '';
  if (raw.startsWith('yt:')) return raw.slice(3);
  return raw;
}

/** Persist selected playlists as namespaced Phase-4 documents. Never writes bare IDs. */
export async function saveImportedPlaylists(uid, playlists, options = {}) {
  if (!uid || !Array.isArray(playlists) || playlists.length === 0) return [];

  const firestore = options.firestore || db;
  const getDocFn = options.getDocFn || getDoc;
  const setDocFn = options.setDocFn || setDoc;
  const payloads = playlists.map(buildImportedPlaylist).filter(Boolean);
  payloads.forEach((payload) => releasePlaylistRemoval(payload.playlistId));

  await Promise.all(payloads.map(async (payload) => {
    const catalogId = catalogIdForImport(payload.playlistId);
    if (!catalogId) return;
    const ref = doc(firestore, 'users', uid, 'playlists', catalogId);
    const existing = await getDocFn(ref);
    if (existing?.exists?.() && isPhase4PlaylistDoc(existing.data() || {})) {
      return;
    }
    const document = migrateOnRead({
      ...payload,
      playlistId: rawIdForImport(payload.playlistId),
      importedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await setDocFn(ref, document);
  }));

  return payloads;
}

export async function fetchImportedPlaylists(uid, options = {}) {
  if (!uid) return [];

  try {
    const snapshot = await getDocs(playlistsCollection(uid));
    const records = (snapshot?.docs ?? []).map((entry) => ({
      id: entry.id,
      data: entry.data() || {},
    }));
    const migrated = await migrateBareYoutubePlaylists(uid, records, {
      firestore: options.firestore || db,
      setDocFn: options.setDocFn,
      deleteDocFn: options.deleteDocFn,
    });
    return dedupeLibraryPlaylistRecords(migrated.records)
      .map((record) => toLibraryListPlaylist(record.id, record.data));
  } catch (error) {
    console.error('Failed to load imported playlists:', describeError(error));
    return [];
  }
}

export async function removeImportedPlaylist(uid, playlistId) {
  if (!uid || !playlistId) return false;
  const ids = new Set([playlistId]);
  if (isBareYouTubePlaylistDoc(playlistId, { source: 'youtube' })) {
    ids.add(userPlaylistId('youtube', playlistId));
  } else if (typeof playlistId === 'string' && playlistId.startsWith('yt:')) {
    ids.add(playlistId.slice(3));
  }
  try {
    await Promise.all([...ids].map((id) => deleteDoc(doc(db, 'users', uid, 'playlists', id))));
    return true;
  } catch (error) {
    console.error('Failed to remove imported playlist:', describeError(error));
    return false;
  }
}
